import capitalize from 'lodash/capitalize';

import { Icons, StatusChip, Tooltip, Stack } from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';

import { PreparedWorkload } from 'features/workload-optimization/types';

import { WorkloadStatusChip } from './WorkloadStatusChip';
import {
  ERROR_STATUS_TYPE,
  LOW_CONFIDENCE_STATUS_TYPE,
  STATUS_BADGE_COLOR_MAP,
} from '../../_constants';
import {
  hasError,
  hasLowConfidence,
  isAppliedWithPods,
  isStopped,
} from '../../_workloadStatusUtils';
import {
  LowConfidenceLastRecommendationMetric,
  LowConfidenceLookBackMetric,
} from '../LowConfidence/LowConfidenceMetrics';
import { LowConfidenceTooltip } from '../LowConfidence/LowConfidenceTooltip';

type Props = {
  testId?: string;
  workload: PreparedWorkload;
  onAppliedStatusClick(): void;
};

export const WorkloadStatus = (props: Props) => {
  const {
    recommendation,
    workloadConfigV2,
    recommendationStatus: { lowConfidence, error },
  } = props.workload;

  const displayStatus =
    isAppliedWithPods(props.workload) || isStopped(props.workload);

  if (
    !hasLowConfidence(props.workload) &&
    !hasError(props.workload) &&
    !displayStatus
  ) {
    return null;
  }

  return (
    <Stack direction="row" gap={8} data-testid={props.testId}>
      {displayStatus && (
        <WorkloadStatusChip
          workload={props.workload}
          onAppliedStatusClick={props.onAppliedStatusClick}
        />
      )}
      {lowConfidence && (
        <LowConfidenceTooltip
          recommendation={recommendation}
          tooltipPlacement="top"
          content={
            <>
              <LowConfidenceLookBackMetric
                resourceConfig={workloadConfigV2?.vpaConfig?.cpu}
                type={ComputeResourceType.CPU}
              />
              <LowConfidenceLastRecommendationMetric
                recommendation={recommendation}
                type={ComputeResourceType.CPU}
              />
            </>
          }
        >
          <StatusChip
            testId="low-confidence-chip"
            sx={STATUS_BADGE_COLOR_MAP[LOW_CONFIDENCE_STATUS_TYPE]}
            iconChip
            size="md"
            onClick={(e) => e.stopPropagation()}
          >
            <Icons.WarningCircle fontSize={16} />
          </StatusChip>
        </LowConfidenceTooltip>
      )}
      {error && (
        <Tooltip title={capitalize(error.message)} placement="top" arrow>
          <StatusChip
            testId="error-chip"
            sx={STATUS_BADGE_COLOR_MAP[ERROR_STATUS_TYPE]}
            iconChip
            size="md"
          >
            <Icons.Warning fontSize={16} />
          </StatusChip>
        </Tooltip>
      )}
    </Stack>
  );
};
